import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Mic, MicOff, Users, Clock, Radio, Video,
  Loader2, CheckCircle2, AlertCircle, LogIn, FileText, Calendar
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MicLevel = ({ level, isRecording }) => {
  const bars = 12;
  return (
    <div className="flex items-end gap-0.5 h-8" data-testid="guest-mic-level">
      {Array.from({ length: bars }).map((_, i) => {
        const threshold = (i / bars) * 100;
        const active = level > threshold && isRecording;
        return (
          <div key={i}
            className={cn(
              "w-1.5 rounded-full transition-all duration-75",
              active ? (i < bars * 0.5 ? "bg-emerald-400" : i < bars * 0.8 ? "bg-amber-400" : "bg-red-400") : "bg-slate-700"
            )}
            style={{ height: `${4 + (i * 2)}px` }}
          />
        );
      })}
    </div>
  );
};

export default function PublicMeetingPage() {
  const { token } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState(false);
  const [guestId, setGuestId] = useState('');
  const [guestName, setGuestName] = useState('');
  const [joining, setJoining] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [micPermission, setMicPermission] = useState('pending');
  const [elapsed, setElapsed] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [transcriptLines, setTranscriptLines] = useState([]);
  const [ended, setEnded] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const chunkSeqRef = useRef(0);
  const animFrameRef = useRef(null);
  const chunkTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const recordingStartRef = useRef(null);
  const recognitionRef = useRef(null);
  const pollRef = useRef(null);
  const autoJoinAttempted = useRef(false);

  // Fetch meeting info
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/meetings/public/${token}`);
        setMeeting(res.data);
        if (res.data.status === 'completed' || res.data.status === 'processing' || res.data.status === 'error') {
          setEnded(true);
        }
      } catch {
        setMeeting(null);
      }
      setLoading(false);
    })();
  }, [token]);

  // Auto-join when meeting has client details pre-filled
  useEffect(() => {
    if (!meeting || joined || autoJoinAttempted.current || loading || ended) return;
    const clientName = meeting.client_name?.trim();
    const clientEmail = meeting.client_email?.trim();
    if (clientName) {
      autoJoinAttempted.current = true;
      handleAutoJoin(clientName, clientEmail || '');
    }
  }, [meeting, joined, loading, ended]); // eslint-disable-line

  // Poll meeting status after joining
  useEffect(() => {
    if (!joined || !meeting) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/meetings/public/${token}`);
        setMeeting(res.data);
        if (res.data.status === 'completed' || res.data.status === 'processing' || res.data.status === 'error') {
          setEnded(true);
          stopLocalRecording();
          clearInterval(pollRef.current);
        }
        // Auto-start recording when meeting becomes active
        if (res.data.status === 'active' && !isRecording && micPermission === 'granted') {
          startLocalRecording();
          startSpeechRecognition();
        }
      } catch { /* */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [joined, meeting?.meeting_id, isRecording, micPermission]); // eslint-disable-line

  const handleAutoJoin = async (name, email) => {
    setJoining(true);
    try {
      const res = await axios.post(`${API}/api/meetings/public/${token}/join`, { name, email });
      setGuestId(res.data.guest_id);
      setGuestName(name);
      setJoined(true);
      setMeeting(prev => ({ ...prev, ...res.data }));
      toast.success('Welcome to the meeting!');
      requestMicPermission();
    } catch {
      // If auto-join fails, let user try manually
      autoJoinAttempted.current = false;
    }
    setJoining(false);
  };

  const requestMicPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setMicPermission('granted');
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      audioContextRef.current = ctx;
      analyserRef.current = analyser;
      const updateLevel = () => {
        const data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setMicLevel((avg / 255) * 100);
        animFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch {
      setMicPermission('denied');
    }
  };

  const startLocalRecording = useCallback(() => {
    if (!streamRef.current || isRecording) return;
    setIsRecording(true);
    chunkSeqRef.current = 0;
    recordingStartRef.current = Date.now();

    const startChunk = () => {
      if (!streamRef.current) return;
      try {
        const mr = new MediaRecorder(streamRef.current, { mimeType: 'audio/webm;codecs=opus' });
        mediaRecorderRef.current = mr;
        const chunks = [];
        const tsStart = Date.now();
        mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
        mr.onstop = async () => {
          if (chunks.length === 0) return;
          const blob = new Blob(chunks, { type: 'audio/webm' });
          const tsEnd = Date.now();
          const seq = chunkSeqRef.current++;
          const fd = new FormData();
          fd.append('meeting_id', meeting?.meeting_id || '');
          fd.append('user_id', guestId);
          fd.append('user_name', guestName);
          fd.append('chunk_seq', seq.toString());
          fd.append('timestamp_start', tsStart.toString());
          fd.append('timestamp_end', tsEnd.toString());
          fd.append('audio', blob, `chunk_${seq}.webm`);
          try { await axios.post(`${API}/api/meetings/public/audio-chunk`, fd); } catch { /* */ }
        };
        mr.start();
        chunkTimerRef.current = setTimeout(() => {
          if (mr.state === 'recording') { mr.stop(); startChunk(); }
        }, 5000);
      } catch { /* */ }
    };
    startChunk();
    elapsedTimerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - recordingStartRef.current) / 1000));
    }, 1000);
  }, [meeting?.meeting_id, guestId, guestName, isRecording]);

  const stopLocalRecording = useCallback(() => {
    setIsRecording(false);
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    if (chunkTimerRef.current) clearTimeout(chunkTimerRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    stopSpeechRecognition();
  }, []);

  const startSpeechRecognition = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          const text = r[0].transcript.trim();
          if (text) {
            setTranscriptLines(prev => [...prev.slice(-20), { speaker: guestName, text }]);
            axios.post(`${API}/api/meetings/public/transcript-chunk`, {
              meeting_id: meeting?.meeting_id,
              user_id: guestId,
              user_name: guestName,
              text,
              timestamp: new Date().toISOString(),
            }).catch(() => {});
          }
        } else {
          interim += r[0].transcript;
        }
      }
      setLiveTranscript(interim);
    };
    recognition.onerror = () => {};
    recognition.onend = () => {
      if (recognitionRef.current) try { recognition.start(); } catch { /* */ }
    };
    try { recognition.start(); } catch { /* */ }
  }, [meeting?.meeting_id, guestId, guestName]);

  const stopSpeechRecognition = useCallback(() => {
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* */ } recognitionRef.current = null; }
    setLiveTranscript('');
  }, []);

  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (audioContextRef.current) audioContextRef.current.close();
      stopLocalRecording();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  if (loading || joining) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-emerald-400 animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-400">{joining ? 'Joining meeting...' : 'Loading meeting...'}</p>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900" data-testid="meeting-not-found">
        <div className="text-center max-w-sm">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white mb-1">Meeting Not Found</h2>
          <p className="text-sm text-slate-400">This meeting link is invalid or the meeting has already ended.</p>
        </div>
      </div>
    );
  }

  if (ended) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900" data-testid="meeting-ended">
        <div className="text-center max-w-sm">
          <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white mb-1">Meeting Ended</h2>
          <p className="text-sm text-slate-400">Thank you for joining! Meeting minutes will be shared with you via email shortly.</p>
          <p className="text-[10px] text-slate-600 mt-3">A PDF copy of the meeting minutes will also be attached.</p>
        </div>
      </div>
    );
  }

  // In-meeting view (guest) — client enters directly, no form needed
  return (
    <div className="min-h-screen bg-slate-900 p-4 md:p-6" data-testid="public-meeting-room">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            {meeting.status === 'active' && <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />}
            <h1 className="text-xl font-bold text-white">{meeting.title}</h1>
          </div>
          <div className="flex items-center justify-center gap-3 text-xs text-slate-400 flex-wrap">
            <span className="flex items-center gap-1"><Users className="h-3 w-3" />Hosted by {meeting.host_name}</span>
            {isRecording && <span className="flex items-center gap-1 text-red-400"><Radio className="h-3 w-3" />REC {formatTime(elapsed)}</span>}
            {guestName && <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Guest: {guestName}</Badge>}
          </div>
        </div>

        {micPermission === 'denied' && (
          <Card className="bg-red-500/10 border-red-500/30 mb-4">
            <CardContent className="p-3 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
              <p className="text-xs text-red-300">Microphone access denied. Allow mic permission to participate.</p>
            </CardContent>
          </Card>
        )}

        {micPermission === 'granted' && (
          <Card className="bg-slate-800/60 border-slate-700/50 mb-4">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", isRecording ? "bg-red-500/20" : "bg-slate-700")}>
                    {isRecording ? <Mic className="h-4 w-4 text-red-400" /> : <MicOff className="h-4 w-4 text-slate-500" />}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-white">Your Microphone</p>
                    <p className="text-[10px] text-slate-500">
                      {isRecording ? 'Recording & transcribing' : meeting.status === 'waiting' ? 'Waiting for host to start' : 'Ready'}
                    </p>
                  </div>
                </div>
                <MicLevel level={micLevel} isRecording={micPermission === 'granted'} />
              </div>
            </CardContent>
          </Card>
        )}

        {isRecording && (transcriptLines.length > 0 || liveTranscript) && (
          <Card className="bg-slate-800/60 border-slate-700/50 mb-4" data-testid="guest-live-transcript">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Live Transcript
              </p>
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {transcriptLines.slice(-8).map((line, i) => (
                  <p key={i} className="text-xs text-slate-300">
                    <span className="text-emerald-400 font-medium">{line.speaker}: </span>{line.text}
                  </p>
                ))}
                {liveTranscript && <p className="text-xs text-slate-500 italic">{liveTranscript}...</p>}
              </div>
            </CardContent>
          </Card>
        )}

        {meeting.status === 'waiting' && (
          <Card className="bg-slate-800/60 border-slate-700/50">
            <CardContent className="p-6 text-center">
              <Loader2 className="h-6 w-6 text-emerald-400 animate-spin mx-auto mb-3" />
              <p className="text-sm text-slate-300">Waiting for the host to start the meeting...</p>
              <p className="text-[10px] text-slate-500 mt-1">Recording will begin automatically when the host starts.</p>
            </CardContent>
          </Card>
        )}

        {meeting.agenda?.length > 0 && (
          <Card className="bg-slate-800/60 border-slate-700/50 mt-4">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-medium">Agenda</p>
              <div className="space-y-1">
                {meeting.agenda.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-slate-300">
                    <span className="w-4 h-4 rounded bg-slate-700 flex items-center justify-center shrink-0 text-[10px] text-slate-500">{i + 1}</span>
                    {item}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
